import { FC, ReactNode } from 'react';
import { ResponsesData, TableBodyProps } from 'features/CustomFieldsResponses/types';
import { downloadTicketQR } from './utils';
import { formatDate } from 'features/common/utils/dateUtils';
import { SYSTEM_FIELD_NAMES } from './constants';
import { AttendeeActions } from '../AttendeeActions';
import {
	PunchupActionsCell,
	PunchupBreakdownCell,
	PunchupDataRow,
	PunchupDateTimeCell,
	PunchupEntityCell,
	PunchupNameCell,
	PunchupTableEmptyRow,
	PunchupTableLoadingRow,
} from 'features/common/components/Punchup/primitives/table/PunchupTable';
import { PunchupTag } from 'features/common/components/Punchup/primitives/display/PunchupTag';
import { PunchupTicketTypeTag } from 'features/common/components/Punchup/primitives/display/PunchupTicketTypeTag';
import { useTicketTypeColors } from 'features/common/hooks/useTicketTypeColors';
import { useTranslation } from 'react-i18next';
import bodyStyles from './PunchupTableBody.module.css';
import {
	formatResponseStatusLabel,
	getResponseStatusBackground,
	getResponseTypeBackground,
	getScannedBackground,
} from './statusColors';

interface PunchupTableBodyWithHeadingsProps extends TableBodyProps {
	visibleHeadings: string[];
	/** True while the initial fetch is in flight (data === undefined). */
	isLoading?: boolean;
	/** Override the default AttendeeActions renderer for a row. Receives the page's order→ticketIds map so the override can drive the bulk "switch all" path. */
	renderRowActions?: (data: ResponsesData, transactionTickets: Record<string, number[]>) => ReactNode;
}

export const PunchupTableBody: FC<PunchupTableBodyWithHeadingsProps> = ({
	responsesQueryObject,
	visibleHeadings,
	isAttendeesList,
	showDownloadQR = false,
	isLoading,
	renderRowActions,
}) => {
	const { t } = useTranslation();
	const { data: responses } = responsesQueryObject;
	// Resolve the type colour from the shared source (the event's ticket types), not the per-row
	// `ticket_type_color` — the responses payload null-fills it, which showed grey tags here.
	const { resolveByName } = useTicketTypeColors();

	if (isLoading || !responses) {
		return (
			<tbody>
				<PunchupTableLoadingRow />
			</tbody>
		);
	}

	const hasData = responses.pages.some((page) => page.length > 0);
	if (!hasData) {
		return (
			<tbody>
				<PunchupTableEmptyRow text={t('customFields-responses-empty', 'No responses found')} />
			</tbody>
		);
	}

	return (
		<tbody>
			{responses.pages.map((page: ResponsesData[]) => {
				const transactionTickets: Record<string, number[]> = {};
				for (const r of page) {
					if (!transactionTickets[r.order_id]) {
						transactionTickets[r.order_id] = [];
					}

					transactionTickets[r.order_id].push(r.ticket_id);
				}
				return page.map((data: ResponsesData) => {
					const filteredResponses = data.responses.filter((r) => !SYSTEM_FIELD_NAMES.includes(r.custom_field.name));

					const seatId =
						data.seat_id ||
						data.tickets
							?.map((ticket) => ticket.seat_id)
							.filter(Boolean)
							?.join(', ') ||
						'-';

					const cellForHeading = (heading: string) => {
						switch (heading) {
							case 'customFields-responses-table-orderId':
								return <td key="orderId">{data.order_id}</td>;
							case 'customFields-responses-table-role':
								return (
									<td key="role">
										<PunchupTag
											size="sm"
											label={data.is_buyer ? 'Buyer' : 'Attendee'}
											background={getResponseTypeBackground(data.is_buyer)}
										/>
									</td>
								);
							case 'customFields-responses-table-email':
								// Email is the attendee's primary, stable identifier → emphasised entity cell.
								return (
									<PunchupEntityCell key="email" title={data.email || undefined}>
										{data.email || '-'}
									</PunchupEntityCell>
								);
							case 'customFields-responses-table-fullName':
								return (
									<PunchupNameCell key="fullName" title={data.full_name || undefined}>
										{data.full_name || '-'}
									</PunchupNameCell>
								);
							case 'customFields-responses-table-phone':
								return (
									<PunchupNameCell key="phone" title={data.phone_number || undefined}>
										{data.phone_number || '-'}
									</PunchupNameCell>
								);
							case 'customFields-responses-table-event':
								return (
									<PunchupNameCell key="event" title={data.event_name || undefined}>
										{data.event_name || '-'}
									</PunchupNameCell>
								);
							case 'customFields-responses-table-amount':
								return <td key="amount">{`$${data.amount.toFixed(2)}`}</td>;
							case 'customFields-responses-table-date-green':
								return <PunchupDateTimeCell key="date">{formatDate(data.date)}</PunchupDateTimeCell>;
							case 'customFields-responses-table-status':
								return (
									<td key="status">
										<PunchupTag
											size="sm"
											label={formatResponseStatusLabel(data.status)}
											background={getResponseStatusBackground(data.status)}
										/>
									</td>
								);
							case 'customFields-responses-table-ticketTypes':
								return (
									<td key="ticketTypes">
										{data.ticket_types_string ? (
											<PunchupTicketTypeTag
												name={data.ticket_types_string}
												color={resolveByName(data.ticket_types_string)}
											/>
										) : null}
									</td>
								);
							case 'customFields-responses-table-scanned': {
								const scanCount = data.scan_count && data.scan_count > 1 ? ` (${data.scan_count}x)` : '';
								const scannedLabel = data.is_validated ? `✓ Scanned${scanCount}` : 'Not Scanned';
								return (
									<td key="scanned">
										<PunchupTag size="sm" label={scannedLabel} background={getScannedBackground(data.is_validated)} />
									</td>
								);
							}
							case 'customFields-responses-table-promotion':
								return (
									<td key="promotion">
										{data.promotion_used ? (
											<div className={bodyStyles.promoCell}>
												<span className={bodyStyles.promoCode}>{data.promotion_used.code}</span>
												<span className={bodyStyles.promoAmount}>-${data.promotion_used.amount.toFixed(2)}</span>
											</div>
										) : (
											'-'
										)}
									</td>
								);
							case 'customFields-responses-table-giftCards':
								return (
									<td key="giftCards">
										{data.gift_cards_used && data.gift_cards_used.length > 0 ? (
											<div className={bodyStyles.promoCell}>
												{data.gift_cards_used.map((gc, idx) => (
													<div key={idx} className={bodyStyles.giftCardEntry}>
														<span className={bodyStyles.giftCardLast4}>****{gc.last4}</span>
														<span className={bodyStyles.giftCardAmount}>-${gc.applied_amount.toFixed(2)}</span>
													</div>
												))}
											</div>
										) : (
											'-'
										)}
									</td>
								);
							case 'customFields-responses-table-addons':
								// Names only, one per line — no per-add-on count (this feed is deduped-by-product
								// with no quantity) and no dollar sum: the sum double-counts money already in the
								// row's Amount and is unreliable until the add-on aggregation API fix lands
								// (TIC-581 / the reporting audit). Mirrors the Transactions add-ons cell's list.
								return (
									<PunchupBreakdownCell key="addons">
										{data.addons_used && data.addons_used.length > 0 ? (
											<div className={bodyStyles.addonsCell}>
												{data.addons_used.map((a, i) => (
													<span key={`${a.name}-${i}`} className={bodyStyles.addonName}>
														{a.name}
													</span>
												))}
											</div>
										) : (
											'-'
										)}
									</PunchupBreakdownCell>
								);
							case 'customFields-responses-table-seat':
								return (
									<td key="seat">
										<div className={bodyStyles.seatCell}>{seatId}</div>
									</td>
								);
							default: {
								// Dynamic custom field question
								const qResponse = filteredResponses.find((r) => r.custom_field.name === heading);
								let value = qResponse?.string_value || '-';
								if (qResponse && qResponse.choice_id > 0) value = qResponse.choice.name;
								return <td key={heading}>{value}</td>;
							}
						}
					};

					return (
						<PunchupDataRow key={`${data.order_id}_${data.ticket_id}`} rowClick>
							{visibleHeadings.map((h) => cellForHeading(h))}
							{isAttendeesList && showDownloadQR && (
								<td key="qr" onClick={(e) => e.stopPropagation()}>
									{data.can_download_qr ? (
										<button
											type="button"
											className={bodyStyles.downloadQrButton}
											onClick={() => downloadTicketQR(data.ticket_id, data.email)}
										>
											{t('download')}
										</button>
									) : (
										'-'
									)}
								</td>
							)}
							<PunchupActionsCell key="actions" onClick={(e) => e.stopPropagation()}>
								{renderRowActions ? (
									renderRowActions(data, transactionTickets)
								) : (
									<AttendeeActions data={data} transactionTickets={transactionTickets} />
								)}
							</PunchupActionsCell>
						</PunchupDataRow>
					);
				});
			})}
		</tbody>
	);
};
